import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { generateText } from "../../utils/text";
import { SelectionBehavior } from "../../utils/behaviors/interfaces";
import { SingleSelectionBehavior } from "../../utils/behaviors/single-selection";
import { MultiSelectionBehavior } from "../../utils/behaviors/multi-selection";
import { Placement } from "../popover/popover";

import { focusSelectedItem, isRovingTabEvent, moveRovingTabIndex } from "../../utils/roving-tabindex";
import { hasSlot } from "../../utils/utils";
import { FormControl } from "../../utils/form";
import { ListItemValueChange } from "../list-item/list-item";

export type DropdownVariant = "content" | "header" | "header-icon-only" | "header-icon-button";
export type PopoverPlacement = "bottom" | "top";

let id = 0;

/**
 * @slot - The items of the dropdown.
 * @slot custom-trigger - An element that replaces the default elements used to interact with the dropdown.
 * @slot selected-icon - An icon we want to show beside the selected option label of the dropdown.
 */
@Component({
    tag: "uic-dropdown",
    styleUrl: "dropdown.scss",
    shadow: true
})
export class Dropdown {
    private labelId = `uic-dropdown-label-${++id}`;
    private helperTextId = `uic-dropdown-helper-text-${id}`;
    private controlId = `uic-dropdown-${id}`;

    private selectionBehavior: SelectionBehavior;
    private popover: HTMLUicPopoverElement;
    private list: HTMLElement;
    private hiddenInputContainer: HTMLElement = null;

    @State()
    hasCustomTrigger = false;

    @State()
    hasLabel = false;
    @State()
    hasHelperText = false;

    @Element() host!: HTMLUicDropdownElement;

    /**
     * The variant to use.
     */
    @Prop({ reflect: true }) variant: DropdownVariant = "content";

    /**
     * The text describing the values to choose from.
     */
    @Prop() label: string;

    /**
     * The name of the control, which is submitted with the form data.
     */
    @Prop() name: string;

    /**
     * The value of the dropdown.
     */
    @Prop({ mutable: true }) value?: string | Array<string>;

    /**
     * The text to display instead of the selected option's value.
     */
    @Prop() selectedText: string;

    /**
     * The text to show if no value is selected.
     */
    @Prop() placeholder?: string;

    /**
     * If set to `true`, allows multiple selected values.
     */
    @Prop() multiple = false;

    /**
     * If set to `true`, the user cannot interact with the dropdown.
     */
    @Prop() disabled = false;

    /**
     * The initial placement of the popover.
     * Only use this property if you want to force an initial direction,
     * since the popover calculates its placement based on available space.
     */
    @Prop() placement: PopoverPlacement = "bottom";

    /**
     * If set to `true`, the dropdown is visible.
     */
    @Prop({ mutable: true }) open = false;

    /**
     * Controls whether integrated popover is hoisted on display or not.
     */
    @Prop() hoist = false;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() required = false;

    /**
     * If set to `true`, the value is considered incorrect.
     * An invalid state will show the error flag.
     */
    @Prop() invalid = false;

    /**
     * Controls if field is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() unsaved = false;

    /**
     * Information with low emphasis.
     */
    @Prop() helperText: string;

    /**
     * Icon alignment
     */
    @Prop() iconPlacement: string | "left" | "right" = "left";

    /**
     * Shown when invalid is `true`.
     */
    @Prop() invalidText: string;

    /**
     * If set to `true` the scrollbars are not hidden when not hovering the scroll panel
     */
    @Prop() scrollbarAutoHideDisabled = false;

    /**
     * Emitted when the value of the dropdown changes.
     */
    @Event({ eventName: "uicChange" }) change: EventEmitter;

    @Watch("multiple")
    handleMultipleChange() {
        this.selectionBehavior = this.multiple ? this.createMultiSelectionBehavior() : this.createSingleSelectionBehavior();

        if (!this.value) {
            return;
        }

        const value = Array.isArray(this.value) ? this.value : [this.value];
        this.value = this.multiple ? value : value[0];
    }

    @Watch("value")
    onValueChanged() {
        this.selectionBehavior.setValue(this.value);
        this.updateHiddenInputs();
    }

    @Watch("label")
    @Watch("helperText")
    onLabelChanged() {
        this.onSlotChange();
    }

    private onSlotChange = () => {
        this.hasHelperText = hasSlot(this.host, "helper-text") || !!(this.invalid ? this.invalidText : this.helperText);
        this.hasLabel = hasSlot(this.host, "label") || !!this.label;
    };

    connectedCallback() {
        this.handleMultipleChange();
    }

    componentDidLoad() {
        this.updateHiddenInputs();
        this.hasCustomTrigger = this.getHasCustomTrigger();
        this.onValueChanged();
        forceUpdate(this.host);
    }

    @Listen("uicItemAdded")
    public onListItemAdded(event: CustomEvent<string>): void {
        if (this.matchesValue(event.detail)) {
            forceUpdate(this.host);
        }
    }

    @Listen("uicItemUpdated")
    public onListItemUpdated(event: CustomEvent<ListItemValueChange>): void {
        const matchesNow = this.matchesValue(event.detail.current);

        if (matchesNow !== this.matchesValue(event.detail.previous)) {
            forceUpdate(this.host);
        }
    }

    /**
     * Tests if provided value equals current value of single-selection list or
     * is included with list of values in a multi-selection list.
     *
     * @param value value to be tested
     * @private
     */
    private matchesValue(value: string): boolean {
        if (value == null) {
            return false;
        }

        if (Array.isArray(this.value)) {
            for (const v of this.value) {
                if (value === v) {
                    return true;
                }
            }

            return false;
        }

        return this.value === value;
    }

    private setUpHiddenInputContainer() {
        this.hiddenInputContainer = document.createElement("span");
        this.host.after(this.hiddenInputContainer);
    }

    private updateHiddenInputs() {
        if (this.hiddenInputContainer === null) {
            this.setUpHiddenInputContainer();
        }
        this.hiddenInputContainer.innerHTML = "";
        if (typeof this.value === "undefined") {
            return;
        }
        const createInput = (value: string): void => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = this.name;
            input.value = value;
            this.hiddenInputContainer.append(input);
        };
        if (Array.isArray(this.value)) {
            this.value.forEach(createInput);
            return;
        }
        createInput(this.value);
    }

    /**
     * Sets focus to an interactable element.
     */
    @Method()
    async setFocus() {
        const focusable = (this.host.querySelector("[slot='custom-trigger'] button") ||
            this.host.shadowRoot.querySelector("button")) as HTMLElement;
        focusable?.focus();
    }

    private handleSlotChange() {
        this.selectionBehavior.setValue(this.value);
        this.hasCustomTrigger = this.getHasCustomTrigger();

        this.onLabelChanged();
    }

    private createSingleSelectionBehavior() {
        return new SingleSelectionBehavior({
            getItems: this.getItems.bind(this),
            getSelectedItem: this.getSelectedItem.bind(this),
            notifySelect: () => this.popover.hide(),
            setValue: (value: string) => this.setValue(value)
        });
    }

    private createMultiSelectionBehavior() {
        return new MultiSelectionBehavior({
            getItems: this.getItems.bind(this),
            getSelectedItem: this.getSelectedItem.bind(this),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setValue: (value: any) => this.setValue(value)
        });
    }

    private setValue(value: string) {
        if (this.value === value) {
            return;
        }

        this.value = value;
        this.change.emit(this.value);
    }

    private getItems() {
        return Array.from(this.host.querySelectorAll<HTMLElement>("[uic-item]"));
    }

    private getSelectedItem() {
        return this.host.querySelector<HTMLElement>("[uic-item][selected]");
    }

    private getText(): string {
        const selectedText = this.selectedText;
        if (selectedText != null && selectedText !== "") {
            return selectedText;
        }
        return generateText(
            this.getItems().filter((el) => !el.hasAttribute("uic-controller")),
            this.value
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleSelect(event: CustomEvent<any>) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const target = (event.detail || event.target) as any;
        this.selectionBehavior.select(target);
    }

    private handleShowPopover() {
        // popover of dropdown should be the same width as its button
        // since the list is hosted by the popover, we must enforce dimensions manually
        // we allow consumers to specify their own offset to account for padding, etc.
        const offset = Number(getComputedStyle(this.list).getPropertyValue("--uic-list-popover-min-width-offset") || 0);

        const minWidth = `${this.host.clientWidth - offset}px`;

        this.list.style.minWidth = minWidth;

        // limit popover content like uic-combobox for the "content" variant
        if (!this.hasCustomTrigger && this.variant === "content") {
            this.list.style.maxWidth = minWidth;
        }
    }

    private handleAfterShowPopover = (e: CustomEvent) => {
        this.open = true;

        // only focus when the popover was trigger by keyboard input
        if (e.detail.triggerType === "key") {
            focusSelectedItem(this.getItems());
        }
    };

    private handleHidePopover() {
        this.open = false;
    }

    private handleKeyDown = (e: KeyboardEvent) => {
        if (isRovingTabEvent(e)) {
            moveRovingTabIndex(e, this.getItems());
        }
    };

    private renderButton() {
        const label = this.label;
        const value = this.getText() || this.placeholder;
        const isPlaceholder = value === this.placeholder;

        const valueClasses = {
            "uic-dropdown__value": true,
            "uic-dropdown__value--placeholder": isPlaceholder
        };

        if (this.variant === "header-icon-only" || this.variant === "header-icon-button") {
            const selectedIcon = this.getSelectedItem()?.getAttribute("icon") || null;
            return (
                <button class='uic-dropdown__button' disabled={this.disabled} aria-label={label}>
                    <div class='uic-dropdown__content'>
                        {selectedIcon && <uic-icon name={selectedIcon}></uic-icon>}
                        {!selectedIcon && <slot name='selected-icon'></slot>}
                    </div>
                    <uic-icon class='uic-dropdown__chevron' name='uic:chevron-down'></uic-icon>
                </button>
            );
        }

        if (this.variant !== "content") {
            return (
                <button class='uic-dropdown__button' disabled={this.disabled}>
                    <div class='uic-dropdown__content'>
                        <span class='uic-dropdown__label'>{label}</span>
                        <slot name='selected-icon'></slot>
                        <span class={valueClasses}>{value}</span>
                    </div>
                    <uic-icon class='uic-dropdown__chevron' name='uic:chevron-down'></uic-icon>
                </button>
            );
        }

        const showMandatoryFlag = this.required && (this.value === null || (Array.isArray(this.value) && this.value.length === 0));

        const iconName = this.invalid
            ? "uic:flag-error"
            : showMandatoryFlag
            ? "uic:flag-mandatory"
            : this.unsaved
            ? "uic:flag-unsaved"
            : null;

        const helperText = this.invalid ? this.invalidText : this.helperText;

        return (
            <FormControl
                label={label}
                helperText={helperText}
                controlId={this.controlId}
                helperTextId={this.helperTextId}
                hasHelperText={this.hasHelperText}
                hasLabel={this.hasLabel}
                invalid={this.invalid}
            >
                <button
                    class='uic-dropdown__button'
                    id={this.controlId}
                    aria-labelledby={this.labelId}
                    aria-describedby={this.helperTextId}
                    disabled={this.disabled}
                >
                    {this.iconPlacement !== "right" && <slot name='selected-icon'></slot>}

                    <span class={valueClasses}>{value}</span>

                    {this.iconPlacement === "right" && <slot name='selected-icon'></slot>}

                    {iconName != null && <uic-icon name={iconName}></uic-icon>}

                    <uic-icon class='uic-dropdown__chevron' name='uic:chevron-down'></uic-icon>
                </button>
            </FormControl>
        );
    }

    private getHasCustomTrigger() {
        return hasSlot(this.host, "custom-trigger");
    }

    private getPopoverProps() {
        const placement = `${this.placement}-start` as Placement;
        let skidding = 0;
        let distance = 4;

        if (this.variant === "content") {
            skidding = 8;
            distance = this.hasHelperText ? -20 : -4;
        }

        return {
            skidding,
            distance,
            placement,
            open: this.open,
            hoist: this.hoist,
            containingElement: this.host,
            onUicShow: () => this.handleShowPopover(),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onUicAfterShow: (e: any) => this.handleAfterShowPopover(e),
            onUicHide: () => this.handleHidePopover()
        };
    }

    render() {
        const renderKnownVariant = !this.hasCustomTrigger;
        const popoverProps = this.getPopoverProps();

        return (
            <Host
                class={{
                    "uic-dropdown--open": this.open,
                    "uic-dropdown--disabled": this.disabled,
                    [`uic-dropdown--variant-${this.variant}`]: renderKnownVariant
                }}
                onKeyDown={(e) => this.handleKeyDown(e)}
            >
                <uic-popover ref={(el) => (this.popover = el)} {...popoverProps}>
                    <div slot='trigger'>
                        <slot name='custom-trigger'>{renderKnownVariant && this.renderButton()}</slot>
                    </div>
                    <uic-list ref={(el) => (this.list = el)} onUicSelect={(e) => this.handleSelect(e)}>
                        <uic-scrollpanel class='uic-dropdown__scroller' scrollbarAutoHideDisabled={this.scrollbarAutoHideDisabled}>
                            <slot onSlotchange={() => this.handleSlotChange()}></slot>
                        </uic-scrollpanel>
                    </uic-list>
                </uic-popover>
            </Host>
        );
    }
}
